import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {RouteService} from '../../../../services/route/route.service';
import {CartService} from '../../../../services/cart/cart.service';
import {StorageService} from '../../../../services/storage/storage.service';
import {Subscription} from 'rxjs';
import {cloneDeep, find, orderBy, uniq} from 'lodash-es';
import {TicketSelectHeaderComponent} from '../ticket-select-header/ticket-select-header.component';
import {Event, EventTicketIds, LegacyTicket, PriceClass, PriceType, SeatInfo, Sector} from '../../../../generated/sales-terminal-client';
import {EventService} from '../../../../services/event/event.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {PricePipe} from '../../../../pipes/price/price.pipe';
import SelectionArea, {ChangedElements, PartialSelectionOptions, SelectionEvent} from '@viselect/vanilla';
import {finalize} from 'rxjs/operators';
import {UtilService} from '../../../../services/util/util.service';
import {BookingsService} from '../../../../services/bookings/bookings.service';
import moment from 'moment';
import {RefundService} from '../../../../services/refund/refund.service';
import {EventPurchaseComponent} from '../event-purchase.component';
import {TranslateService} from '@ngx-translate/core';
import {Privileges} from '../../../admin/privileges/privileges.constants';
import {TicketTypeService} from '../../../../services/ticket-type/ticket-type.service';
import {ViewService} from "../../../../services/view/view.service";
import SeatStatusEnum = SeatInfo.SeatStatusEnum;

export interface DetailedSector extends Omit<Sector, 'priceClasses'> {
  seatsInfo?: SeatInfo[];
  priceClasses?: DetailedPriceClass[];
}

export interface DetailedPriceClass extends PriceClass {
  countString: string;
  priceString: string;
  options: PriceType[];
}

@Component({
  selector: 'app-ticket-select',
  templateUrl: './ticket-select.component.html',
  styleUrls: ['./ticket-select.component.scss']
})
export class TicketSelectComponent implements OnInit, OnDestroy {
  public static STORAGE_EVENT_PURCHASE_SUGGEST = 'eventPurchase.suggest';
  public static STORAGE_EVENT_PURCHASE_SELECTED_SECTOR = 'eventPurchase.selectedSector';
  public static STORAGE_EVENT_PURCHASE_ACTIVE_SECTOR = 'eventPurchase.activeSector';

  private readonly _selectionConfiguration: PartialSelectionOptions = {
    startAreas: ['html'],
    selectionAreaClass: 'selection',
    behaviour: {
      startThreshold: 10,
      overlap: 'keep',
      scrolling: {
        speedDivider: 10,
        manualSpeed: 750,
        startScrollMargins: { x: 0, y: 0 }
      }
    },
    features: {
      touch: true,
      singleTap: {
        // Enable single-click selection (Also disables range-selection via shift + ctrl).
        allow: true,
        // 'native' (element was mouse-event target) or 'touch' (element visually touched).
        intersect: 'touch'
      }
    },
    // intersect: 'touch',
    selectables: ['.piletilevi_venue_map_canvas .place'],
    boundaries: ['.piletilevi_venue_map_canvas'],
    container: 'mat-sidenav-content',
  };

  public selectCollapsed: boolean;
  public selectedView: string;
  public enabledSectorIds: number[] = [];
  public selectedPriceClassIds: number[] = [];
  public activeSectorId: number;
  public suggestEnabled: boolean;
  public hasVenueMap: boolean;
  public selectedSeatsAmount: number;
  public disableCanvasDragging: boolean;
  public updateMiniVenueMap: number;
  public customerName: string;
  private _customerNameCache: Record<string, string> = {};
  private _bulkSelectionActive: boolean;
  private _bulkSeatIds: number[] = [];
  private _bulkSelection: SelectionArea;
  private _previousBulkActionChange: ChangedElements;
  public suggestedTickets: Record<string, Record<string, number>> = {};
  private _scrollTop: number;
  private _subscriptions: Subscription[] = [];
  public displayVenueMap: boolean = true;
  public loadingSectorId: number;

  @Input() event: Event;
  @Input() isLoading: boolean;
  @Input() refundVisible: boolean;

  private _hideContent: boolean = this.routeService.isWindow;
  @Input()
  get hideContent(): boolean {
    return this._hideContent;
  }
  set hideContent(value: boolean) {
    this._hideContent = value;
    this.hideContentChange.emit(this._hideContent);
    this.routeService.header = {
      title: value ? (this.tickets.length ? this.translateService.instant('general.api_cart') : '') : this.event && this.event.title || '',
      date: value ? null : this.event && this.event.event || null
    };
  }
  @Output() hideContentChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private _refundMode: boolean;
  @Input()
  public get refundMode(): boolean {
    return this._refundMode;
  }
  public set refundMode(value: boolean) {
    this._refundMode = value;
    if (value) {
      this.refundModeClear();
      this.enableAreaSelection();
    }
    this.refundModeChange.emit(this._refundMode);
  }
  @Output() refundModeChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private _selectedSector: DetailedSector;
  public get selectedSector(): DetailedSector {
    return this._selectedSector;
  }
  public set selectedSector(value: DetailedSector) {
    this._selectedSector = value;
    this.selectedSectorChange.emit(this._selectedSector);
    // this.displayVenueMap = this.hasVenueMap && value?.hasSeats; // PJSTELTR-10247: Always show venue map by default
  }
  @Output() selectedSectorChange: EventEmitter<DetailedSector> = new EventEmitter<DetailedSector>();

  private _selectedSeatIds: number[] = [];
  @Input()
  public get selectedSeatIds(): number[] {
    return this._selectedSeatIds;
  }
  public set selectedSeatIds(value: number[]) {
    this.deselectedSeatIds = this._selectedSeatIds.filter(seatId => !value.includes(seatId));
    this._selectedSeatIds = value;
    this.selectedSeatIdsChange.emit(this._selectedSeatIds);
  }
  @Output() selectedSeatIdsChange: EventEmitter<number[]> = new EventEmitter<number[]>();

  private _deselectedSeatIds: number[] = [];
  @Input()
  public get deselectedSeatIds(): number[] {
    return this._deselectedSeatIds;
  }
  public set deselectedSeatIds(value: number[]) {
    this._deselectedSeatIds = value;
    this.deselectedSeatIdsChange.emit(this._deselectedSeatIds);
  }
  @Output() deselectedSeatIdsChange: EventEmitter<number[]> = new EventEmitter<number[]>();

  public _eventSectors: Sector[] = [];
  public get eventSectors(): Sector[] {
    return this._eventSectors;
  }
  public set eventSectors(value: Sector[]) {
    this._eventSectors = value;
    this.eventSectorsChange.emit(this._eventSectors);
  }
  @Output() eventSectorsChange: EventEmitter<Sector[]> = new EventEmitter<Sector[]>();

  @Output() isLoadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() updateEventDetails: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateSeats: EventEmitter<any> = new EventEmitter<any>();
  @Output() removeSeats: EventEmitter<LegacyTicket[]> = new EventEmitter<LegacyTicket[]>();

  constructor(public routeService: RouteService,
              public authService: AuthService,
              public cartService: CartService,
              public utilService: UtilService,
              private pricePipe: PricePipe,
              private eventService: EventService,
              private translateService: TranslateService,
              private bookingsService: BookingsService,
              private refundService: RefundService,
              private storageService: StorageService,
              private ticketTypeService: TicketTypeService,
              public viewService: ViewService) {}

  public ngOnInit(): void {
    if (!this.routeService.isWindow) {
      this.routeService.reloadExternalWindowIfOpen();
    }
    this.initEventInfo();
    this._subscriptions.push(this.translateService.onLangChange.subscribe(() => {
      this.initEventInfo();
    }));
    if (this.routeService.isWindow) {
      this.hideContent = !parseInt(this.storageService.get(RouteService.STORAGE_ROUTE_IS_PURCHASE_VIEW), 0);
      if (this.hideContent) {
        this.storageService.remove(EventPurchaseComponent.STORAGE_EVENT_PURCHASE_ID);
        this.storageService.remove(EventPurchaseComponent.STORAGE_EVENT_IS_BO);
      } else {
        this.initSavedSelections();
      }
      this.viewService.triggerCdr();
    }
    this._subscriptions.push(this.storageService.changes.subscribe(({ key, value }) => {
      if (key === TicketSelectComponent.STORAGE_EVENT_PURCHASE_SELECTED_SECTOR) {
        this.handleSectorSelection(value, false);
      }
      if (key === TicketSelectComponent.STORAGE_EVENT_PURCHASE_ACTIVE_SECTOR) {
        this.activeSectorId = +this.storageService.get(TicketSelectComponent.STORAGE_EVENT_PURCHASE_ACTIVE_SECTOR);
      }
      if (key === CartService.STORAGE_CART_PROMOCODE_REMOVED) {
        this.initEventInfo();
      }
      if (key === CartService.STORAGE_CART_PRESERVE_GIFTS) {
        if (this.cartService.caller) {
          this.cartService.caller = false;
        } else if (value) {
          this.cartService.preserveGiftTickets = true;
        }
      }
      if (key === TicketSelectComponent.STORAGE_EVENT_PURCHASE_SUGGEST) {
        if (this.cartService.caller) {
          this.cartService.caller = false;
        } else {
          this.suggestedTickets = UtilService.parseJSON(value);
        }
      }
      if (key === CartService.STORAGE_CART_CLEAR) {
        this.initEventInfo();
      }
      this.viewService.triggerCdr();
    }));
  }
  public ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
    this._subscriptions = [];
    if (this._bulkSelection) {
      this._bulkSelection.destroy();
    }
    delete this._bulkSelection;
  }

  private initEventInfo(): void {
    this.eventSectors = [];
    this.enabledSectorIds = [];
    if (this.event) {
      const previouslyStoredEvent: number = Number(this.storageService.get(EventPurchaseComponent.STORAGE_EVENT_PURCHASE_ID)) || null;
      this.storageService.set(EventPurchaseComponent.STORAGE_EVENT_PURCHASE_ID, this.event.eventId);
      this.storageService.set(EventPurchaseComponent.STORAGE_EVENT_IS_BO, this.event.boEvent);
      if (!this.event.boEvent) {
        this.eventService.getEventSectors(this.event.eventId).subscribe((eventSectors: Sector[]) => {
          this.eventSectors = eventSectors;
          this.eventSectors.forEach((sector) => {
            if (sector.enabled && (!sector.isBox || this.authService.checkPrivileges(Privileges.LOGE_VIEW))) {
              if (sector.hasSeats && !sector.selectableSeats) {
                sector.selectableSeats = true;
              }
              this.enabledSectorIds.push(sector.id);
            }
            return sector;
          });
          if (!this.routeService.isWindow && this.event.eventId === previouslyStoredEvent) {
            this.initSavedSelections();
          } else {
            this.storageService.remove(TicketSelectHeaderComponent.STORAGE_EVENT_PURCHASE_SELECTED_VIEW);
            this.storageService.remove(TicketSelectComponent.STORAGE_EVENT_PURCHASE_SELECTED_SECTOR);
            this.storageService.remove(TicketSelectComponent.STORAGE_EVENT_PURCHASE_ACTIVE_SECTOR);
            this.storageService.remove(TicketSelectComponent.STORAGE_EVENT_PURCHASE_SUGGEST);
            this.selectedSector = null;
            if (this.enabledSectorIds.length === 1) {
              this.selectSectorById(this.enabledSectorIds[0], false);
            }
          }
          this.initAreaSelection();
        });
      }
    } else {
      this.selectCollapsed = true;
    }
  }

  private initSavedSelections(): void {
    if (this.enabledSectorIds.length === 1 || this.selectedSector) {
      this.selectSectorById(this.selectedSector?.id || this.enabledSectorIds[0], false);
    } else {
      this.selectSectorById(this.storageService.get(TicketSelectComponent.STORAGE_EVENT_PURCHASE_SELECTED_SECTOR), false);
    }
    if (this.storageService.get(TicketSelectComponent.STORAGE_EVENT_PURCHASE_SUGGEST)) {
      const value = this.storageService.get(TicketSelectComponent.STORAGE_EVENT_PURCHASE_SUGGEST);
      this.suggestedTickets = UtilService.parseJSON(value);
    }
    this.updateSeats.emit();
  }

  private initAreaSelection(): void {
    if (!this._bulkSelection) {
      this._bulkSelection = new SelectionArea(this._selectionConfiguration)
        .on('start', selectionEvent => this.handleStart(selectionEvent))
        .on('move', selectionEvent => this.handleMove(selectionEvent))
        .on('stop', selectionEvent => this.handleStop(selectionEvent));
      this._bulkSelection.disable();
      this.disableCanvasDragging = false;
    }
  }

  private handleStop(selectionEvent: SelectionEvent) {
    this._bulkSelectionActive = false;
    this.selectedSeatsAmount = null;
    const seats = this.getSelectedSeatIds(selectionEvent);
    if (this._bulkSeatIds.length) {
      this._bulkSeatIds = uniq(this._bulkSeatIds.concat(seats));
    } else {
      this._bulkSeatIds = uniq(seats);
    }
    this.updateMiniVenueMap = moment.now();
    if (!this.refundMode) {
      this.bulkSave();
    }
  }

  private handleMove(selectionEvent: SelectionEvent) {
    if (!this._previousBulkActionChange) {
      this._previousBulkActionChange = {
        added: [],
        removed: []
      };
    }
    const selected = [], deselected = [];
    for (const element of selectionEvent.store.changed.added) {
      const seatId = this.getPlaceId(element);
      if (seatId && !this._previousBulkActionChange.added.includes(element)) {
        selected.push(seatId);
      }
    }
    for (const element of selectionEvent.store.changed.removed) {
      const seatId = this.getPlaceId(element);
      if (seatId && !this._previousBulkActionChange.removed.includes(element)) {
        deselected.push(seatId);
      }
    }
    if (selected.length) {
      this.selectedSeatIds = uniq(this._bulkSeatIds.concat(selected));
    }
    if (deselected.length) {
      this.deselectedSeatIds = deselected;
    }
    if (this._bulkSelectionActive) {
      this.selectedSeatsAmount = this.getSelectedSeatIds(selectionEvent).length;
    }
    this._previousBulkActionChange = cloneDeep(selectionEvent.store.changed);

  }

  private handleStart(selectionEvent: SelectionEvent) {
    this._bulkSeatIds = this.tickets.map(t => t.seatId);
    this._bulkSelectionActive = true;
    if (!selectionEvent.event.ctrlKey && !selectionEvent.event.metaKey) {
      for (const element of selectionEvent.store.selected) {
        const seatId = this.getPlaceId(element);
        if (seatId) {
          this.deselectedSeatIds.push(this.getPlaceId(element));
        }
        this._bulkSelection.deselect(element);
      }
      this._bulkSelection.clearSelection();
    }
  }

  private bulkSave(): void {
    const seatsToAdd = this._bulkSeatIds
      .filter(seatId => !find(this.tickets, { seatId: seatId }))
      .map(seatId => find(this.selectedSector.seatsInfo, { id: seatId }) || null)
      .filter(seat => !!seat);
    const ticketsToRemove = this.tickets.filter(ticket => this.deselectedSeatIds.indexOf(ticket.seatId) !== -1);
    this.deselectedSeatIds = [];
    if (seatsToAdd.length) {
      this.addSeats(seatsToAdd);
    }
    if (ticketsToRemove.length) {
      this.removeSeats.emit(ticketsToRemove);
      this.cartService.caller = true;
    }
    this._bulkSeatIds = [];
  }

  private get tickets(): LegacyTicket[] {
    return this.cartService.cart && this.cartService.cart?.events?.find(e => e.id === this.event?.eventId)?.legacyTickets || [];
  }

  public selectSectorById(sectorId: string | number, ignoreSameSector: boolean = true) {
    this.storageService.set(TicketSelectComponent.STORAGE_EVENT_PURCHASE_SELECTED_SECTOR, sectorId);
    this.handleSectorSelection(sectorId, ignoreSameSector);
  }

  public handleSectorSelection(inputSectorId: string | number, ignoreSameSector: boolean = true): void {
    const sectorId = parseInt(inputSectorId?.toString(), 0);
    if (!sectorId) {
      this.selectedView = TicketSelectHeaderComponent.VIEW_SECTORS;
      if (this.utilService.isMobile) {
        if (!this.selectedSector) {return;}
        this.backToSectorSelection();
      }
    } else {
      if (this.loadingSectorId === sectorId) {return;}
      this.loadingSectorId = sectorId;
      this.selectedView = TicketSelectHeaderComponent.VIEW_TICKETS;
      if (ignoreSameSector && this.selectedSector?.id === sectorId) {return;}
      this.tryToUpdateSuggest(false);
      this.eventService.getEventSeatInfo(this.event?.eventId, sectorId).pipe(
        finalize(() => this.loadingSectorId = null)
      ).subscribe((seatInfo) => {
        const currentSector: DetailedSector = find(this.eventSectors, { id: sectorId }) as DetailedSector;
        if (currentSector) {
          currentSector.seatsInfo = seatInfo.map(seat => {
            if (this.selectedSeatIds.includes(seat.id) && (!seat.seatStatus || seat.seatStatus !== SeatStatusEnum.Available)) {
              seat.seatStatus = SeatStatusEnum.Available;
            }
            return seat;
          });
          if (currentSector.priceClasses) {
            this.selectedPriceClassIds = [];
            currentSector.priceClasses.forEach((sectorPriceClass: DetailedPriceClass) => {
              sectorPriceClass.countString = this.eventService.getFreePlacesString(sectorPriceClass.freePlaces);
              sectorPriceClass.priceString = this.pricePipe.transform(sectorPriceClass.price);
              sectorPriceClass.options = orderBy(sectorPriceClass.priceTypes, ['id'], ['asc']);
              this.selectedPriceClassIds.push(sectorPriceClass.id);
              return sectorPriceClass;
            });
            this.suggestEnabled = !currentSector.priceClasses.every(priceClass => priceClass.hasSeats);
          }
          this.selectedSector = currentSector;
          if (!this.suggestEnabled && !this.refundMode) {
            this.enableAreaSelection();
          }
        }
      });
    }
    this.storageService.set(TicketSelectHeaderComponent.STORAGE_EVENT_PURCHASE_SELECTED_VIEW, this.selectedView);
  }

  public hoverOnActiveSector(sectionId: number = null): void {
    this.storageService.set(TicketSelectComponent.STORAGE_EVENT_PURCHASE_ACTIVE_SECTOR, sectionId);
  }

  public backToSectorSelection(): void {
    this.selectedView = TicketSelectHeaderComponent.VIEW_SECTORS;
    this.storageService.set(TicketSelectHeaderComponent.STORAGE_EVENT_PURCHASE_SELECTED_VIEW, this.selectedView);
    this.refundMode = false;
    setTimeout(() => {
      this.selectedSector = null;
      this.storageService.remove(TicketSelectComponent.STORAGE_EVENT_PURCHASE_SELECTED_SECTOR);
      this.storageService.remove(TicketSelectComponent.STORAGE_EVENT_PURCHASE_ACTIVE_SECTOR);
    }, 100);
  }

  public addSeatById(seatId: number): void {
    const seat: SeatInfo = find(this.selectedSector.seatsInfo, { id: seatId });
    if (seat) {
      if (this.refundMode) {
        this._bulkSeatIds.push(seatId);
        this.selectedSeatIds.push(seatId);
        this._bulkSelection.select('#place_' + seatId);
        this.selectedSeatsAmount = null;
      } else {
        this.addSeats([seat]);
      }
    }
  }

  public removeSeatById(seatId: number): void {
    const seat: SeatInfo = find(this.selectedSector.seatsInfo, { id: seatId });
    if (seat) {
      if (this.refundMode) {
        this._bulkSeatIds.splice(this._bulkSeatIds.indexOf(seatId), 1);
        this.selectedSeatIds.splice(this.selectedSeatIds.indexOf(seatId), 1);
        this._bulkSelection.getSelection().forEach(element => {
          if (this.getPlaceId(element) === seatId) {
            this._bulkSelection.deselect(element);
          }
        });
      } else {
        this.removeSeats.emit([{ id: seat.ticketId }]);
        this.cartService.caller = true;
      }
    }
  }

  private addSeats(seats: SeatInfo[]): void {
    this.startLoader();
    const ticketIds: EventTicketIds = { tickets: [], eventId: this.event?.eventId };
    seats.forEach(seat => {
      ticketIds.tickets.push(seat.ticketId);
    });
    this.cartService.addTickets(ticketIds).pipe(
      finalize(() => this.stopLoader())
    ).subscribe(cart => {
      this.cartService.lastEventId = this.event?.eventId;
      seats.forEach(seat => {
        this.selectedSeatIds.push(seat.id);
      });
      this.updateCartWithCaller();
    });
  }

  public tryToUpdateSuggest(isCaller = true): void {
    if (isCaller) {
      this.cartService.caller = true;
    }
    this.storageService.set(TicketSelectComponent.STORAGE_EVENT_PURCHASE_SUGGEST, JSON.stringify(cloneDeep(this.suggestedTickets)));
  }
  private enableAreaSelection() {
    if (this.utilService.isMobile) {
      this.disableAreaSelection();
    } else {
      this._bulkSelection.enable();
      this.disableCanvasDragging = true;
    }
  }

  private disableAreaSelection() {
    this._bulkSelection.disable();
    this.disableCanvasDragging = false;
  }

  private getPlaceId(element: Element): number {
    const seatId = parseFloat(element.getAttribute('id').toString().split('_')[1]);
    const seat = find(this.selectedSector.seatsInfo, { id: seatId, seatStatus: !this.refundMode ? SeatStatusEnum.Available : SeatStatusEnum.Sold });
    return seat && seatId || null;
  }

  public get getEnabledSectors(): Sector[] {
    return this.eventSectors.filter(sector => (this.enabledSectorIds.includes(sector.id)));
  }

  public getSelectedSeatIds(event: SelectionEvent): number[] {
    return event.store.selected.map(seat => this.getPlaceId(seat)).filter(seatId => seatId);
  }

  public openBooking(bookingId: number): void {
    if (bookingId) {
      this.routeService.navigate('/app/sales-terminal/bookings/view/' + bookingId);
    }
  }

  public updateBookingClient(bookingId: number): void {
    if (!bookingId || !this.authService.checkPrivileges(Privileges.VENUEMAP_REFUND)) {
      this.customerName = '-';
      return;
    }
    if (this._customerNameCache[bookingId]) {
      this.customerName = this._customerNameCache[bookingId];
      return;
    }
    this._customerNameCache[bookingId] = '-';
    this.bookingsService.searchSimple(null, bookingId).subscribe((res) => {
      if (res && res[0].customer) {
        this.customerName = res[0].customer.firstName + ' ' + res[0].customer.lastName;
        this._customerNameCache[bookingId] = this.customerName;
      }
    });
  }

  public refundModeSave(): void {
    const ticketIdsToRefund: number[] = this._bulkSeatIds
      .filter(seatId => !find(this.tickets, { seatId: seatId }))
      .map(seatId => find(this.selectedSector.seatsInfo, { id: seatId }) as SeatInfo || null)
      .filter(seat => seat)
      .map(seat => seat.ticketId);
    if (ticketIdsToRefund.length) {
      this.refundService.addTickets({ tickets: ticketIdsToRefund }).subscribe(_ => {
        this.routeService.navigate('/app/sales-terminal/refund');
        this.refundModeClear();
        this.refundMode = false;
      });
    } else {
      this.refundModeClear();
      this.refundMode = false;
    }
  }

  public refundModeClear(): void {
    this._previousBulkActionChange = {
      added: [],
      removed: []
    };
    this.selectedSeatsAmount = null;
    this.selectedSeatIds = [];
    this.deselectedSeatIds = this._bulkSeatIds;
    this._bulkSeatIds = [];
    this._bulkSelection.clearSelection();
    this.updateMiniVenueMap = moment.now();
  }

  public openCustomerScreen(): void {
    this.backToSectorSelection();
    this.routeService.openInExternalWindow();
  }

  public get showPromocode(): boolean {
    return !this.routeService.isWindow && this.event?.status?.id !== 6;
  }

  public promocodeChanged(): void {
    this.ticketTypeService.resetTicketTypeCounts();
    this.updateCartWithCaller();
    this.initEventInfo();
  }

  public startLoader(): void {
    this._scrollTop = cloneDeep(this.utilService.contentScrolledTop);
    this.isLoading = true;
    this.isLoadingChange.emit(this.isLoading);
  }

  public stopLoader(): void {
    this.storageService.set(UtilService.STORAGE_CONTENT_SCROLL, this._scrollTop);
    this.isLoading = false;
    this.isLoadingChange.emit(this.isLoading);
  }

  public updateCartWithCaller(): void {
    this.cartService.caller = true;
    this.cartService.updateCartForEvent(this.event.eventId);
    this.storageService.set(CartService.STORAGE_CART_UPDATE, moment.now());
  }
}
